/**
 * DealCloseHoldContext — RW-2440: closing a deal waits a few seconds so it can
 * be undone, app-wide.
 *
 * Closing cannot be taken back by re-opening. changeStatus files the deal's
 * clients as past clients and fills in their home, anniversaries and Sold
 * address (RW-2598), marks the listing Sold, tells the deal team and nudges
 * the agent about vendors. Re-opening moves the stages back but none of the
 * rest. So Undo does not mean "re-open": the close is not sent until the
 * window passes (the undo-send pattern), and Undo inside it means nothing was
 * ever written.
 *
 * The hold lives here, beside the toasts, not in a screen. Its Undo toast is
 * app-wide, so the hold must be too: a hold kept by the board or the deal
 * record was sent the moment you opened another page, while its Undo stayed on
 * screen and quietly did nothing (the #1255 review). Now leaving a page leaves
 * the hold running. What ends it early is the app going away, and then it is
 * released to send rather than dropped: sign-out releases it first
 * (flushHeldDealCloses, while still signed in — App waits up to 10 s), and so
 * does the page being hidden — a phone switching apps or tabs, which never
 * fires beforeunload. Released means queued: behind any move on the deal still
 * unanswered, so on a phone that suspends a background page it goes out on
 * resume. A reload or a closed tab is best effort only: the SDK gathers tokens
 * before it fetches, so the request may never leave the page. That is why a
 * desktop browser asks first.
 *
 * Every stage change on a deal — board moves, record moves, and the close
 * this hold sends — goes through ONE queue per deal, in the order it was
 * made. changeStatus reads the stage without a lock, so two in flight can
 * land in either order: a close sent while an earlier move was still queued
 * (a hidden page sends at once) overtook it, and the move then re-opened a
 * deal whose closing side effects had already fired (the #1255 round-3 review).
 *
 *   hold(dealId, { label, word })  — hold a close, toast "Marking … Closed…" + Undo
 *   undo(dealId)                   — drop it; nothing was sent
 *   cancel(dealId)                 — drop it quietly (a different move replaces it)
 *   enqueue(dealId, task)          — run a stage change behind everything already
 *                                    queued for the deal; resolves with its result
 *   confirmClosed(dealId)          — a surface's data now shows it closed: end the bridge
 *   heldIds / sentIds              — for surfaces that show the deal in Closed
 *                                    before their own data catches up
 *   landedRev                      — bumps when a close's reply is in, so a surface
 *                                    whose data showed "closed" first confirms it then
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from './ToastContext';
import { dealsService } from '../services/deals';
import { friendlyCallableError } from '../lib/callableErrors';
import { stageMovePayload } from '../lib/dealStageMoves';

export const CLOSE_UNDO_MS = 8000;
// ToastContext's exit animation. The close is sent once the Undo toast has
// fully gone, so its Undo is never a button that does nothing.
export const TOAST_EXIT_MS = 300;
const SENT_BRIDGE_MS = 15000;

const EMPTY = new Set();
const DealCloseHoldContext = createContext(null);

const without = (set, id) => { const next = new Set(set); next.delete(id); return next; };

// The mounted provider's flush, for sign-out (App's handleLogout) — which
// lives above the provider and must send what is held before signOut.
let flushRegistered = null;
export function flushHeldDealCloses() {
  return flushRegistered ? flushRegistered() : Promise.resolve([]);
}

export function DealCloseHoldProvider({ children, delayMs = CLOSE_UNDO_MS }) {
  const { showToast, dismissToast } = useToast();
  const held = useRef(new Map()); // dealId → { timer, label, word, toastId }
  const queues = useRef(new Map()); // dealId → the tail of its stage-change queue
  const closing = useRef(new Map()); // dealId → closes queued or on their way
  const bridges = useRef(new Map()); // dealId → the timer ending its bridge
  const latestSend = useRef(new Map()); // dealId → the send whose bridge is current
  const [heldIds, setHeldIds] = useState(EMPTY);
  // Closes sent this session: a surface whose own data hasn't refetched yet
  // keeps showing the deal in Closed instead of flicking it back.
  const [sentIds, setSentIds] = useState(EMPTY);
  // The server bumps the deals signal before it replies (the team emails and
  // nudges come in between), so a surface's data can show "closed" while the
  // close is still unanswered — too early to end the bridge. This bumps when
  // the reply is in, so those surfaces confirm again (the #1255 round-4 review).
  const [landedRev, setLandedRev] = useState(0);

  const unbridge = useCallback((dealId) => {
    clearTimeout(bridges.current.get(dealId));
    bridges.current.delete(dealId);
    latestSend.current.delete(dealId);
    setSentIds((prev) => (prev.has(dealId) ? without(prev, dealId) : prev));
  }, []);

  const take = useCallback((dealId) => {
    const h = held.current.get(dealId);
    if (!h) return null;
    clearTimeout(h.timer);
    held.current.delete(dealId);
    setHeldIds((prev) => without(prev, dealId));
    // The hold is over, however it ended — its Undo toast goes with it.
    if (dismissToast && h.toastId != null) dismissToast(h.toastId);
    return h;
  }, [dismissToast]);

  // The deal's one queue: a task runs once everything queued before it for
  // that deal has finished, however that went.
  const enqueue = useCallback((dealId, task) => {
    const next = (queues.current.get(dealId) || Promise.resolve()).then(() => task());
    const tail = next.catch(() => {});
    queues.current.set(dealId, tail);
    tail.then(() => { if (queues.current.get(dealId) === tail) queues.current.delete(dealId); });
    return next;
  }, []);

  const send = useCallback((dealId, h) => {
    clearTimeout(bridges.current.get(dealId));
    bridges.current.delete(dealId);
    const mine = {};
    latestSend.current.set(dealId, mine);
    setSentIds((prev) => new Set(prev).add(dealId));
    closing.current.set(dealId, (closing.current.get(dealId) || 0) + 1);
    return enqueue(dealId, async () => {
      try {
        await dealsService.changeStatus(dealId, stageMovePayload(null, 'closed'));
        showToast(`${h.label} is ${h.word.toLowerCase()}`, 'success');
        // A bridge, not a record: surfaces refetch on the deals signal within
        // seconds (and end it sooner with confirmClosed); a later re-open must
        // not be painted Closed from here. Only the current send's bridge gets
        // a timer — an earlier close answered late must not end a later one's.
        if (latestSend.current.get(dealId) === mine) {
          bridges.current.set(dealId, setTimeout(() => unbridge(dealId), SENT_BRIDGE_MS));
        }
      } catch (err) {
        if (latestSend.current.get(dealId) === mine) unbridge(dealId);
        showToast(friendlyCallableError(err, `mark ${h.label} ${h.word.toLowerCase()}`).headline, 'error');
      } finally {
        const left = (closing.current.get(dealId) || 1) - 1;
        if (left) closing.current.set(dealId, left); else closing.current.delete(dealId);
        setLandedRev((n) => n + 1);
      }
    });
  }, [enqueue, showToast, unbridge]);

  const undo = useCallback((dealId) => {
    if (!take(dealId)) return false;
    showToast('Still open — nothing was closed', 'info');
    return true;
  }, [take, showToast]);

  const cancel = useCallback((dealId) => {
    unbridge(dealId); // a close just sent is replaced by the new move too
    return !!take(dealId);
  }, [take, unbridge]);

  // Not while this session's own close is still queued or on its way: data
  // showing "closed" then predates it (a deal re-opened and closed again).
  const confirmClosed = useCallback((dealId) => {
    if (!closing.current.has(dealId)) unbridge(dealId);
  }, [unbridge]);

  const hold = useCallback((dealId, { label = 'This deal', word = 'Closed' } = {}) => {
    if (held.current.has(dealId)) return;
    const h = { label, word };
    h.timer = setTimeout(() => {
      if (take(dealId)) send(dealId, h);
    }, delayMs + TOAST_EXIT_MS);
    held.current.set(dealId, h);
    setHeldIds((prev) => new Set(prev).add(dealId));
    h.toastId = showToast(`Marking ${label} ${word}…`, 'info', delayMs, {
      action: { label: 'Undo', onClick: () => undo(dealId) },
    });
  }, [delayMs, take, send, undo, showToast]);

  // Send every held close now, rather than drop one the agent meant.
  const flushAll = useCallback(() => Promise.all([...held.current.keys()].map((dealId) => {
    const h = take(dealId);
    return h ? send(dealId, h) : null;
  })), [take, send]);

  useEffect(() => {
    flushRegistered = flushAll;
    return () => { if (flushRegistered === flushAll) flushRegistered = null; };
  }, [flushAll]);

  // The last resort: the provider went away with closes still held. Sign-out
  // and a hidden page have flushed in the ordinary case; this send may be
  // refused if auth has already gone.
  useEffect(() => () => {
    for (const [dealId, h] of [...held.current.entries()]) {
      clearTimeout(h.timer);
      held.current.delete(dealId);
      dealsService.changeStatus(dealId, stageMovePayload(null, 'closed')).catch(() => {});
    }
    for (const timer of bridges.current.values()) clearTimeout(timer);
  }, []);

  const holding = heldIds.size > 0;
  useEffect(() => {
    if (!holding) return undefined;
    // Closing the tab on a desktop browser asks first…
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    // …but a phone never asks, and a hidden page may never come back: send now.
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushAll(); };
    const onPageHide = () => { flushAll(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [holding, flushAll]);

  const value = useMemo(() => ({
    hold, undo, cancel, enqueue, confirmClosed, heldIds, sentIds, landedRev,
  }), [hold, undo, cancel, enqueue, confirmClosed, heldIds, sentIds, landedRev]);
  return <DealCloseHoldContext.Provider value={value}>{children}</DealCloseHoldContext.Provider>;
}

export function useDealCloseHold() {
  const ctx = useContext(DealCloseHoldContext);
  // Loud, like useToast: a missing provider would otherwise mean a close that
  // is quietly never sent.
  if (!ctx) throw new Error('useDealCloseHold must be used within a DealCloseHoldProvider');
  return ctx;
}
